import { cn } from '@/lib/utils';
import { EDataSourceStatus } from '@/models/dataSource';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { LuFileSpreadsheet, LuPencil, LuTrash2 } from 'react-icons/lu';
import { CitizensCount } from '../components/CitizensCount';
import { ComuniList } from '../useComuniLists';

const columnHelper = createColumnHelper<ComuniList>();

const STATUS_DOTS: Partial<Record<EDataSourceStatus, string>> = {
  [EDataSourceStatus.ACTIVE]: 'bg-green-500',
  [EDataSourceStatus.PENDING]: 'bg-yellow-500',
  [EDataSourceStatus.ERROR]: 'bg-red-500',
};

const STATUS_LABEL_KEYS: Partial<Record<EDataSourceStatus, string>> = {
  [EDataSourceStatus.ACTIVE]: 'lists.status.active',
  [EDataSourceStatus.PENDING]: 'lists.status.processing',
  [EDataSourceStatus.ERROR]: 'lists.status.error',
};

export const ListStatusPill: FC<{ status: EDataSourceStatus; t: TFunction }> = ({ status, t }) => {
  const labelKey = STATUS_LABEL_KEYS[status];

  return (
    <span className='inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[13.5px] font-medium text-primary-900'>
      <span className={cn('size-[9px] rounded-full', STATUS_DOTS[status])} />
      {labelKey ? t(labelKey) : status}
    </span>
  );
};

type GetUploadedListsColumnsArgs = {
  t: TFunction;
  onOpenMapping: (list: ComuniList) => void;
  onRequestDelete: (list: ComuniList) => void;
};

export const getUploadedListsColumns = ({
  t,
  onOpenMapping,
  onRequestDelete,
}: GetUploadedListsColumnsArgs): ColumnDef<ComuniList>[] =>
  [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => t('lists.columns.name'),
      cell: ({ row }) => {
        const list = row.original;
        return (
          <div className='flex items-center gap-4'>
            <LuFileSpreadsheet className='size-9 flex-none text-brand-700' strokeWidth={1.6} />
            <div className='min-w-0'>
              <div className='truncate text-[17px] font-medium text-primary-900'>{list.name}</div>
              <div className='mt-0.5 text-sm text-quaternary-400'>{list.datasources[0]?.type?.name ?? '—'}</div>
            </div>
          </div>
        );
      },
      minSize: 280,
      maxSize: 480,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'rows',
      header: () => t('lists.columns.rows'),
      cell: ({ row }) => {
        const list = row.original;
        return (
          <span className='font-medium text-primary-900'>
            <CitizensCount datasourceId={list.datasources.length === 1 ? list.datasources[0].id : undefined} />
          </span>
        );
      },
      minSize: 100,
      maxSize: 140,
      enableSorting: false,
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => t('lists.columns.status'),
      cell: ({ getValue }) => <ListStatusPill status={getValue()} t={t} />,
      minSize: 140,
      maxSize: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('createdOn', {
      id: 'createdOn',
      header: () => t('lists.columns.createdOn'),
      cell: ({ getValue }) => (
        <span className='text-tertiary-600'>{DateTime.fromISO(getValue()).toFormat('dd/LL/yyyy')}</span>
      ),
      minSize: 120,
      maxSize: 160,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => '',
      cell: ({ row }) => {
        const list = row.original;
        return (
          <span className='inline-flex items-center gap-3'>
            <button
              type='button'
              title={t('lists.mapping')}
              onClick={() => onOpenMapping(list)}
              className='p-1.5 text-tertiary-600 hover:text-brand-700'
            >
              <LuPencil className='size-5' />
            </button>
            <button
              type='button'
              title={t('lists.delete')}
              onClick={() => onRequestDelete(list)}
              className='p-1.5 text-tertiary-600 hover:text-red-600'
            >
              <LuTrash2 className='size-5' />
            </button>
          </span>
        );
      },
      minSize: 100,
      maxSize: 120,
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ] as ColumnDef<ComuniList>[];
